import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import PageTitle from '../../FormElements/PageTitle';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import FilterByPosition from '../../Filters/FilterByPosition';
import FilterByTeamMultiSelect from '../../Filters/FilterByTeamMultiSelect';
import FilterBySearchInput from '../../Filters/FilterBySearchInput';
import FilterByIsRookie from '../../Filters/FilterByIsRookie';
import ClearFiltersButton from '../../Filters/ClearFilterButton/ClearFiltersButton';
import PaginationTracker from '../../Pagination/PaginationTracker/PaginationTracker';
import PaginationTrackerMobile from '../../Pagination/PaginationTrackerMobile/PaginationTrackerMobile';
import PerPageDropdown from '../../Pagination/PerPageDropdown/PerPageDropdown';
import { playerAvatarImgClassName } from '../../../utils/avatarClassNames';
import {
	getCachedTeamLogoSrc,
	prefetchTeamLogos,
	subscribeTeamLogoCache,
	TEAM_LOGO_CACHE_SIZE
} from '../../../utils/teamLogoCache';
import {
	loadRosterIds,
	saveRosterIds
} from '../../../utils/eliminatorRosterStorage';
import {
	ensureDraftSlotsWithUndrafted,
	getTeamSlotEntryIds,
	loadDraftSlots,
	saveDraftSlots,
	seedDraftSlotsFromEntries,
	swapDraftSlots
} from '../../../utils/eliminatorDraftBoard';
import EliminatorRosterRail from '../../EliminatorPlayerMatchups/EliminatorRosterRail';
import EliminatorDraftBoardRail from '../../EliminatorPlayerMatchups/EliminatorDraftBoardRail';

const RANKINGS_AVATAR_SIZE = 32;
const ELIMINATOR_WEEKS = Array.from({ length: 17 }, (_, index) => index + 1);
const DEFAULT_SEASON = new Date().getFullYear();
const PROJECTION_EDITOR_EMAIL = 'mhoelzel23@gmail.com';

const POSITION_BUTTONS = [
	{ label: 'All', value: 'all' },
	{ label: 'QB', value: 'quarterback' },
	{ label: 'RB', value: 'running back' },
	{ label: 'WR', value: 'wide receiver' },
	{ label: 'TE', value: 'tight end' }
];

const POSITION_FILTER_TO_ABBR = {
	quarterback: 'QB',
	'running back': 'RB',
	'wide receiver': 'WR',
	'tight end': 'TE'
};

const POSITION_ABBR_TO_PROFILE = {
	QB: 'quarterback',
	RB: 'running back',
	WR: 'wide receiver',
	TE: 'tight end'
};

const FAVORABILITY = {
	favorable: {
		label: 'Favorable',
		dotClass: 'bg-green-500'
	},
	neutral: {
		label: 'Neutral',
		dotClass: 'bg-yellow-400'
	},
	unfavorable: {
		label: 'Unfavorable',
		dotClass: 'bg-red-500'
	}
};

const FAVORABILITY_ORDER = ['favorable', 'neutral', 'unfavorable'];

const TABLE_WRAPPER_CLASS =
	'w-full border border-gray-300 dark:border-dark-border rounded-md overflow-x-auto bg-white dark:bg-dark-card text-gray-800 dark:text-dark-text';

const TABLE_HEAD_CLASS = 'bg-gray-100 dark:bg-gray-800 sticky top-0 z-20';

const STICKY_HEAD_CELL_CLASS =
	'px-4 py-3 text-left text-gray-800 dark:text-dark-text border-b border-gray-300 dark:border-dark-border font-semibold bg-gray-100 dark:bg-gray-800 sticky left-0 z-30';

const WEEK_HEAD_CELL_CLASS =
	'px-2 py-3 text-center text-gray-800 dark:text-dark-text border-b border-l border-gray-300 dark:border-dark-border font-semibold whitespace-nowrap w-20';

const STICKY_BODY_CELL_CLASS =
	'px-4 py-2 text-left text-gray-800 dark:text-dark-text whitespace-nowrap bg-white dark:bg-dark-card sticky left-0 z-10';

const WEEK_BODY_CELL_CLASS =
	'px-2 py-2 text-center border-l border-gray-200 dark:border-dark-border w-20';

const MATCHUP_ROW_CLASS =
	'border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card transition-colors hover:bg-gray-50 dark:hover:bg-gray-800';

const COLOR_ROW_CLASS =
	'bg-white dark:bg-dark-card transition-colors hover:bg-gray-50 dark:hover:bg-gray-800';

const rankingsAvatarWrapperStyle = {
	width: `${RANKINGS_AVATAR_SIZE}px`,
	height: `${RANKINGS_AVATAR_SIZE}px`,
	overflow: 'hidden',
	marginRight: '8px',
	flexShrink: 0
};

const rankingsAvatarImgStyle = {
	width: `${RANKINGS_AVATAR_SIZE}px`,
	height: `${RANKINGS_AVATAR_SIZE}px`,
	minWidth: `${RANKINGS_AVATAR_SIZE}px`,
	minHeight: `${RANKINGS_AVATAR_SIZE}px`
};

function getCsrfToken() {
	return document.querySelector('[name="csrf-token"]')?.content;
}

function buildEndpoint(base, params) {
	const url = new URL(base, window.location.origin);
	Object.entries(params).forEach(([key, value]) => {
		if (value != null && value !== '') {
			url.searchParams.set(key, value);
		}
	});
	return url.toString();
}

function buildEndpointWithMode(endpoint, mode) {
	if (!endpoint) return endpoint;
	const separator = endpoint.includes('?') ? '&' : '?';
	return `${endpoint}${separator}mode=${encodeURIComponent(mode)}`;
}

function playerProfilePath(entry) {
	if (!entry.player_id) return null;

	const position = POSITION_ABBR_TO_PROFILE[entry.position] || entry.position;
	return `/players/${entry.player_id}?position=${encodeURIComponent(position)}`;
}

function matchesPositionFilter(entry, filterPosition) {
	const allCount = POSITION_BUTTONS.length - 1;
	if (filterPosition.length === 0 || filterPosition.length === allCount) {
		return true;
	}

	const entryFilterValue = Object.entries(POSITION_FILTER_TO_ABBR).find(
		([, abbr]) => abbr === entry.position
	)?.[0];

	return entryFilterValue ? filterPosition.includes(entryFilterValue) : false;
}

function normalizeTeamKey(value) {
	return (value || '').toString().trim().toLowerCase();
}

function matchesTeamFilter(entry, teamFilter, teamLookup) {
	if (!teamFilter) return true;

	const selectedTeams = teamFilter.split(',').filter(Boolean);
	if (selectedTeams.length === 0) return true;

	const entryTeam = teamLookup[normalizeTeamKey(entry.team_name)];
	if (!entryTeam) {
		return selectedTeams.includes(entry.team_name);
	}

	return selectedTeams.some((selected) => {
		const selectedTeam = teamLookup[normalizeTeamKey(selected)];
		if (selectedTeam) return selectedTeam.id === entryTeam.id;
		return normalizeTeamKey(selected) === normalizeTeamKey(entry.team_name);
	});
}

function matchesRookieFilter(entry, isRookieFilter) {
	if (!isRookieFilter) return true;
	return entry.is_rookie === true;
}

function matchesSearchFilter(entry, search) {
	const query = search.trim().toLowerCase();
	if (!query) return true;

	return (
		entry.full_name.toLowerCase().includes(query) ||
		(entry.team_name || '').toLowerCase().includes(query)
	);
}

function filterRankingEntries(entries, filter, search, teamLookup) {
	return entries.filter(
		(entry) =>
			matchesPositionFilter(entry, filter.position) &&
			matchesTeamFilter(entry, filter.team, teamLookup) &&
			matchesRookieFilter(entry, filter.isRookie) &&
			matchesSearchFilter(entry, search)
	);
}

function buildTeamLookup(teams) {
	const lookup = {};
	const aliases = {
		jac: 'jax',
		wsh: 'was'
	};

	teams.forEach((team) => {
		const keys = [team.name, team.abbreviation, team.location].filter(Boolean);
		keys.forEach((key) => {
			lookup[normalizeTeamKey(key)] = team;
		});
	});

	Object.entries(aliases).forEach(([alias, canonical]) => {
		if (lookup[canonical] && !lookup[alias]) {
			lookup[alias] = lookup[canonical];
		}
	});

	return lookup;
}

function getWeekMatchup(team, week) {
	if (!team?.weeks) return null;
	return team.weeks[week] || team.weeks[String(week)] || null;
}

function nextFavorability(current) {
	if (!current) return 'favorable';
	const index = FAVORABILITY_ORDER.indexOf(current);
	if (index < 0) return 'favorable';
	if (index === FAVORABILITY_ORDER.length - 1) return null;
	return FAVORABILITY_ORDER[index + 1];
}

function reorderEntries(entries, draggedId, targetId) {
	if (draggedId === targetId) return entries;

	const next = [...entries];
	const fromIndex = next.findIndex((entry) => entry.id === draggedId);
	const toIndex = next.findIndex((entry) => entry.id === targetId);
	if (fromIndex < 0 || toIndex < 0) return entries;

	const [moved] = next.splice(fromIndex, 1);
	next.splice(toIndex, 0, moved);

	return next.map((entry, index) => ({
		...entry,
		rank: index + 1
	}));
}

function normalizeFavorabilities(favorabilities) {
	const source = favorabilities || {};
	return ELIMINATOR_WEEKS.reduce((memo, week) => {
		const value = source[week] ?? source[String(week)] ?? null;
		memo[String(week)] = FAVORABILITY_ORDER.includes(value) ? value : null;
		return memo;
	}, {});
}

function cloneEntriesForEdit(entries) {
	return entries.map((entry) => ({
		...entry,
		favorabilities: normalizeFavorabilities(entry.favorabilities)
	}));
}

function buildSparseUpdatePayload(baselineEntries, draftEntries) {
	const baselineById = baselineEntries.reduce((memo, entry) => {
		memo[entry.id] = entry;
		return memo;
	}, {});

	const ranks = {};
	const favorabilities = {};

	draftEntries.forEach((entry) => {
		const baseline = baselineById[entry.id];
		if (!baseline) return;

		if (entry.rank !== baseline.rank) {
			ranks[entry.id] = entry.rank;
		}

		const baselineFav = normalizeFavorabilities(baseline.favorabilities);
		const draftFav = normalizeFavorabilities(entry.favorabilities);
		const weekDiffs = {};

		ELIMINATOR_WEEKS.forEach((week) => {
			const key = String(week);
			if (draftFav[key] !== baselineFav[key]) {
				weekDiffs[key] = draftFav[key];
			}
		});

		if (Object.keys(weekDiffs).length > 0) {
			favorabilities[entry.id] = weekDiffs;
		}
	});

	return { ranks, favorabilities };
}

function mergePatchedEntries(currentEntries, patchedEntries) {
	const patchedById = patchedEntries.reduce((memo, entry) => {
		memo[entry.id] = {
			...entry,
			favorabilities: normalizeFavorabilities(entry.favorabilities)
		};
		return memo;
	}, {});

	return currentEntries
		.map((entry) => patchedById[entry.id] || entry)
		.sort((left, right) => left.rank - right.rank);
}

function PlayerAvatar({ entry }) {
	if (entry.image_url) {
		return (
			<div style={rankingsAvatarWrapperStyle}>
				<img
					className={playerAvatarImgClassName}
					src={`${entry.image_url}?w=${RANKINGS_AVATAR_SIZE}&h=${RANKINGS_AVATAR_SIZE}`}
					alt={entry.full_name}
					style={rankingsAvatarImgStyle}
				/>
			</div>
		);
	}

	const initials = `${(entry.first_name || '')[0] || ''}${
		(entry.last_name || '')[0] || ''
	}`.toUpperCase();

	return (
		<div style={rankingsAvatarWrapperStyle}>
			<div
				className={`${playerAvatarImgClassName} flex items-center justify-center text-xs font-semibold text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-800`}
				style={rankingsAvatarImgStyle}
				aria-hidden="true"
			>
				{initials || '?'}
			</div>
		</div>
	);
}

function ShowAvatarsToggle({ isChecked, onChange }) {
	return (
		<div className="flex items-center mr-4 my-3 md:my-4 text-gray-800 dark:text-dark-text">
			<span className="mr-2 text-sm md:text-base text-gray-800 dark:text-dark-text">
				Avatars
			</span>
			<input
				type="checkbox"
				checked={isChecked}
				onChange={(event) => onChange(event.target.checked)}
				className="hidden"
				id="eliminatorShowAvatarsCheckbox"
			/>
			<label
				htmlFor="eliminatorShowAvatarsCheckbox"
				className="cursor-pointer select-none relative"
			>
				<div
					className={`w-8 h-4 md:w-12 md:h-6 rounded-full shadow-inner ${
						isChecked ? 'bg-gray-800' : 'bg-gray-300'
					}`}
				/>
				<div
					className={`dot absolute top-0 left-0 w-4 h-4 md:w-6 md:h-6 bg-white rounded-full shadow transition transform ${
						isChecked
							? 'translate-x-full border border-gray-500'
							: 'border border-gray-300'
					}`}
				/>
			</label>
		</div>
	);
}

function ShowWeeks15To17Toggle({ isChecked, onChange }) {
	return (
		<div className="flex items-center mr-4 my-3 md:my-4 text-gray-800 dark:text-dark-text">
			<span className="mr-2 text-sm md:text-base text-gray-800 dark:text-dark-text whitespace-nowrap">
				Weeks 15–17
			</span>
			<input
				type="checkbox"
				checked={isChecked}
				onChange={(event) => onChange(event.target.checked)}
				className="hidden"
				id="eliminatorShowWeeks15To17Checkbox"
			/>
			<label
				htmlFor="eliminatorShowWeeks15To17Checkbox"
				className="cursor-pointer select-none relative"
			>
				<div
					className={`w-8 h-4 md:w-12 md:h-6 rounded-full shadow-inner ${
						isChecked ? 'bg-gray-800' : 'bg-gray-300'
					}`}
				/>
				<div
					className={`dot absolute top-0 left-0 w-4 h-4 md:w-6 md:h-6 bg-white rounded-full shadow transition transform ${
						isChecked
							? 'translate-x-full border border-gray-500'
							: 'border border-gray-300'
					}`}
				/>
			</label>
		</div>
	);
}

function OpponentLogo({ team, sizeClassName = 'h-7 w-7', size = TEAM_LOGO_CACHE_SIZE }) {
	const imageUrl = team?.image_url;
	const [src, setSrc] = useState(() => getCachedTeamLogoSrc(imageUrl, size));

	useEffect(() => {
		if (!imageUrl) {
			setSrc(null);
			return undefined;
		}

		prefetchTeamLogos([imageUrl], size);
		setSrc(getCachedTeamLogoSrc(imageUrl, size));

		return subscribeTeamLogoCache(() => {
			setSrc(getCachedTeamLogoSrc(imageUrl, size));
		});
	}, [imageUrl, size]);

	if (!src) {
		return <div className={`flex-shrink-0 ${sizeClassName}`} aria-hidden="true" />;
	}

	return (
		<img
			src={src}
			alt={`${team.abbreviation || team.name} logo`}
			className={`flex-shrink-0 object-contain ${sizeClassName}`}
			onError={(event) => {
				event.currentTarget.style.display = 'none';
			}}
		/>
	);
}

function MatchupCell({ matchup, opponentTeam, hasSchedule }) {
	if (!hasSchedule) {
		return (
			<span className="text-xs font-semibold text-gray-400 dark:text-gray-500">
				—
			</span>
		);
	}

	if (!matchup) {
		return (
			<span className="text-xs font-semibold text-gray-400 dark:text-gray-500">
				BYE
			</span>
		);
	}

	const venueLabel = matchup.is_home_game ? 'vs' : '@';

	return (
		<div className="flex flex-col items-center justify-center gap-1">
			<span className="text-2xs font-bold uppercase text-gray-500 dark:text-gray-400">
				{venueLabel}
			</span>
			<OpponentLogo team={opponentTeam} />
		</div>
	);
}

function FavorabilityCell({
	favorability,
	isEditing,
	disabled,
	onCycle
}) {
	if (disabled && !favorability) {
		return (
			<span className="text-xs font-semibold text-gray-400 dark:text-gray-500">
				—
			</span>
		);
	}

	if (!favorability) {
		if (!isEditing) {
			return (
				<span className="text-xs font-semibold text-gray-400 dark:text-gray-500">
					—
				</span>
			);
		}

		return (
			<button
				type="button"
				onClick={onCycle}
				className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-gray-500 hover:text-gray-600 dark:border-gray-600"
				title="Set favorability"
				aria-label="Set favorability"
			>
				+
			</button>
		);
	}

	const meta = FAVORABILITY[favorability];
	const dot = (
		<span
			className={`inline-block h-3 w-3 rounded-full ${meta.dotClass}`}
			title={meta.label}
			aria-label={meta.label}
		/>
	);

	if (!isEditing || disabled) {
		return dot;
	}

	return (
		<button
			type="button"
			onClick={onCycle}
			className="inline-flex h-5 w-5 items-center justify-center rounded-full hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-600"
			title={`${meta.label} — click to change`}
			aria-label={`Change favorability from ${meta.label}`}
		>
			{dot}
		</button>
	);
}

function PlayerMatchupRows({
	entry,
	weeks,
	scheduleTeam,
	teamsById,
	showAvatars,
	stickyOffsets,
	isEditing,
	isDragging,
	isDropTarget,
	isOnRoster,
	onAddToRoster,
	onDragStart,
	onDragOver,
	onDrop,
	onDragEnd,
	onCycleFavorability
}) {
	const profilePath = playerProfilePath(entry);
	const favorabilities = normalizeFavorabilities(entry.favorabilities);

	const rowClassName = [
		MATCHUP_ROW_CLASS,
		isEditing ? 'cursor-move' : '',
		isDragging ? 'opacity-50 bg-blue-50 dark:bg-gray-800' : '',
		isDropTarget ? 'bg-blue-100 dark:bg-gray-700' : ''
	]
		.filter(Boolean)
		.join(' ');

	return (
		<>
			<tr
				className={rowClassName}
				draggable={isEditing}
				onDragStart={onDragStart}
				onDragOver={onDragOver}
				onDrop={onDrop}
				onDragEnd={onDragEnd}
			>
				<td
					rowSpan={2}
					className={STICKY_BODY_CELL_CLASS}
					style={{ left: stickyOffsets.rank }}
				>
					{isEditing && (
						<span className="mr-2 text-gray-400 dark:text-gray-500" aria-hidden="true">
							☰
						</span>
					)}
					{entry.rank}
				</td>
				<td
					rowSpan={2}
					className={`${STICKY_BODY_CELL_CLASS} border-r border-gray-200 dark:border-dark-border`}
					style={{ left: stickyOffsets.player }}
				>
					<div className="flex items-center">
						{showAvatars && <PlayerAvatar entry={entry} />}
						{profilePath ? (
							<a
								href={profilePath}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-500 hover:underline"
								onMouseDown={(event) => event.stopPropagation()}
								onDragStart={(event) => event.preventDefault()}
							>
								{entry.full_name}
							</a>
						) : (
							entry.full_name
						)}
						{isOnRoster ? (
							<span
								className="ml-2 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-2xs font-bold text-green-700 dark:bg-green-900 dark:bg-opacity-40 dark:text-green-300"
								title="On roster"
								aria-label="On roster"
							>
								✓
							</span>
						) : (
							<button
								type="button"
								onClick={(event) => {
									event.stopPropagation();
									onAddToRoster(entry.id);
								}}
								onMouseDown={(event) => event.stopPropagation()}
								className="ml-2 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-sm font-semibold leading-none text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-card dark:text-dark-text dark:hover:bg-gray-800"
								title="Add to roster"
								aria-label={`Add ${entry.full_name} to roster`}
							>
								+
							</button>
						)}
					</div>
				</td>
				<td
					rowSpan={2}
					className={`${STICKY_BODY_CELL_CLASS} border-r border-gray-200 dark:border-dark-border`}
					style={{ left: stickyOffsets.pos }}
				>
					{entry.position}
				</td>
				{weeks.map((week) => {
					const matchup = getWeekMatchup(scheduleTeam, week);
					const opponentTeam = matchup
						? teamsById[matchup.opponent_id]
						: null;

					return (
						<td key={`matchup-${entry.id}-${week}`} className={WEEK_BODY_CELL_CLASS}>
							<MatchupCell
								matchup={matchup}
								opponentTeam={opponentTeam}
								hasSchedule={Boolean(scheduleTeam)}
							/>
						</td>
					);
				})}
			</tr>
			<tr className={COLOR_ROW_CLASS}>
				{weeks.map((week) => {
					const matchup = scheduleTeam ? getWeekMatchup(scheduleTeam, week) : null;
					const isBye = Boolean(scheduleTeam) && !matchup;

					return (
						<td
							key={`favorability-${entry.id}-${week}`}
							className={`${WEEK_BODY_CELL_CLASS} pb-3`}
						>
							<FavorabilityCell
								favorability={favorabilities[String(week)]}
								isEditing={isEditing}
								disabled={isBye}
								onCycle={() => onCycleFavorability(entry.id, week)}
							/>
						</td>
					);
				})}
			</tr>
		</>
	);
}

export default function EliminatorPlayerMatchupsPage({
	get_eliminator_rankings_endpoint,
	update_eliminator_rankings_endpoint,
	import_eliminator_rankings_endpoint,
	export_eliminator_rankings_endpoint,
	export_eliminator_defense_grades_endpoint,
	export_eliminator_defense_matrix_endpoint,
	apply_eliminator_defense_grades_endpoint,
	get_championship_weeks_endpoint
}) {
	const { user, authenticated } = useAuth();

	const [entries, setEntries] = useState([]);
	const [draftEntries, setDraftEntries] = useState([]);
	const [teams, setTeams] = useState([]);
	const [weeks, setWeeks] = useState(ELIMINATOR_WEEKS);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [isApplyingDefenseGrades, setIsApplyingDefenseGrades] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const [filter, setFilter] = useState({ position: [], team: '', isRookie: false });
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [perPage, setPerPage] = useState(25);
	const [showAvatars, setShowAvatars] = useState(true);
	const [showWeeks15To17, setShowWeeks15To17] = useState(false);
	const [rosterIds, setRosterIds] = useState(() => loadRosterIds(DEFAULT_SEASON));
	const [activeRail, setActiveRail] = useState(null);
	const [draftSlots, setDraftSlots] = useState(() => loadDraftSlots(DEFAULT_SEASON));
	const [draggedId, setDraggedId] = useState(null);
	const [dropTargetId, setDropTargetId] = useState(null);
	const editBaselineRef = useRef([]);
	const draftSeededRef = useRef(false);
	const fileInputRef = useRef(null);

	const canEdit =
		authenticated &&
		(user?.email === PROJECTION_EDITOR_EMAIL);
	const isEditingMode = canEdit && isEditing;

	const stickyOffsets = useMemo(
		() => ({
			rank: 0,
			player: '3.5rem',
			pos: '14.5rem'
		}),
		[]
	);

	const loadData = useCallback(async () => {
		if (!get_eliminator_rankings_endpoint || !get_championship_weeks_endpoint) {
			return;
		}

		setIsLoading(true);
		setError('');

		try {
			const [rankingsResponse, scheduleResponse] = await Promise.all([
				fetch(
					buildEndpoint(get_eliminator_rankings_endpoint, {
						season: DEFAULT_SEASON
					}),
					{
						credentials: 'same-origin',
						headers: { Accept: 'application/json' }
					}
				),
				fetch(buildEndpointWithMode(get_championship_weeks_endpoint, 'eliminator'), {
					credentials: 'same-origin',
					headers: { Accept: 'application/json' }
				})
			]);

			const rankingsPayload = await rankingsResponse.json();
			const schedulePayload = await scheduleResponse.json();

			if (!rankingsResponse.ok || !rankingsPayload.success) {
				throw new Error(
					rankingsPayload.error || 'Failed to load eliminator rankings.'
				);
			}

			if (!scheduleResponse.ok || !schedulePayload.success) {
				throw new Error(
					schedulePayload.error || 'Failed to load survival schedule.'
				);
			}

			const nextTeams = schedulePayload.data?.teams || [];
			const nextEntries = (rankingsPayload.data?.entries || []).map((entry) => ({
				...entry,
				favorabilities: normalizeFavorabilities(entry.favorabilities)
			}));

			setEntries(nextEntries);
			setTeams(nextTeams);
			setWeeks(
				Array.isArray(schedulePayload.data?.weeks) &&
					schedulePayload.data.weeks.length > 0
					? schedulePayload.data.weeks
					: ELIMINATOR_WEEKS
			);
			prefetchTeamLogos(
				nextTeams.map((team) => team.image_url),
				TEAM_LOGO_CACHE_SIZE
			);
		} catch (loadError) {
			setError(loadError.message || 'Failed to load eliminator matchups.');
			setEntries([]);
			setTeams([]);
		} finally {
			setIsLoading(false);
		}
	}, [get_eliminator_rankings_endpoint, get_championship_weeks_endpoint]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	useEffect(() => {
		saveRosterIds(DEFAULT_SEASON, rosterIds);
	}, [rosterIds]);

	useEffect(() => {
		if (!draftSlots) return;
		saveDraftSlots(DEFAULT_SEASON, draftSlots);
	}, [draftSlots]);

	useEffect(() => {
		if (entries.length === 0) return;

		const validIds = new Set(entries.map((entry) => entry.id));
		setRosterIds((prev) => {
			const next = prev.filter((id) => validIds.has(id));
			return next.length === prev.length ? prev : next;
		});

		if (!draftSeededRef.current) {
			draftSeededRef.current = true;
			setDraftSlots((prev) => {
				if (prev) {
					const sanitized = prev.map((id) =>
						id != null && validIds.has(id) ? id : null
					);
					const filled = sanitized.filter((id) => id != null).length;
					if (filled > 0) {
						return ensureDraftSlotsWithUndrafted(sanitized, entries);
					}
				}
				return seedDraftSlotsFromEntries(entries);
			});
			return;
		}

		setDraftSlots((prev) => {
			if (!prev) return seedDraftSlotsFromEntries(entries);
			const sanitized = prev.map((id) =>
				id != null && validIds.has(id) ? id : null
			);
			const next = ensureDraftSlotsWithUndrafted(sanitized, entries);
			if (
				next.length === prev.length &&
				next.every((id, index) => id === prev[index])
			) {
				return prev;
			}
			return next;
		});
	}, [entries]);

	useEffect(() => {
		if (!canEdit) {
			setIsEditing(false);
			setDraftEntries([]);
			editBaselineRef.current = [];
			setDraggedId(null);
			setDropTargetId(null);
		}
	}, [canEdit]);

	const teamLookup = useMemo(() => buildTeamLookup(teams), [teams]);

	const teamsById = useMemo(() => {
		return teams.reduce((lookup, team) => {
			lookup[team.id] = team;
			return lookup;
		}, {});
	}, [teams]);

	const visibleWeeks = useMemo(() => {
		if (showWeeks15To17) return weeks;
		return weeks.filter((week) => week <= 14);
	}, [weeks, showWeeks15To17]);

	const sourceEntries = isEditingMode ? draftEntries : entries;

	const entriesById = useMemo(() => {
		return sourceEntries.reduce((lookup, entry) => {
			lookup[entry.id] = entry;
			return lookup;
		}, {});
	}, [sourceEntries]);

	const rosterIdSet = useMemo(() => new Set(rosterIds), [rosterIds]);

	const rosterEntries = useMemo(() => {
		return rosterIds
			.map((id) => entriesById[id])
			.filter(Boolean)
			.map((entry) => ({
				...entry,
				favorabilities: normalizeFavorabilities(entry.favorabilities)
			}));
	}, [rosterIds, entriesById]);

	const filteredEntries = useMemo(
		() => filterRankingEntries(sourceEntries, filter, search, teamLookup),
		[sourceEntries, filter, search, teamLookup]
	);

	const totalPages = Math.max(1, Math.ceil(filteredEntries.length / perPage) || 1);

	const paginatedEntries = useMemo(() => {
		const start = (page - 1) * perPage;
		return filteredEntries.slice(start, start + perPage);
	}, [filteredEntries, page, perPage]);

	const displayEntries = isEditingMode ? filteredEntries : paginatedEntries;

	useEffect(() => {
		setPage(1);
	}, [filter, search, perPage, entries.length]);

	useEffect(() => {
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, totalPages]);

	const handleFilterChange = (filterKey, value) => {
		setFilter((prevFilter) => ({ ...prevFilter, [filterKey]: value }));
	};

	const handleSearchInputChange = (event) => {
		setSearch(event.target.value);
	};

	const handleClearFilters = () => {
		setFilter({ position: [], team: '', isRookie: false });
		setSearch('');
		setPage(1);
	};

	const handleStartEdit = () => {
		if (!canEdit) return;
		const cloned = cloneEntriesForEdit(entries);
		editBaselineRef.current = cloneEntriesForEdit(entries);
		setDraftEntries(cloned);
		setIsEditing(true);
		setSuccessMessage('');
		setError('');
	};

	const handleCancelEdit = () => {
		setDraftEntries([]);
		editBaselineRef.current = [];
		setIsEditing(false);
		setDraggedId(null);
		setDropTargetId(null);
	};

	const handleCycleFavorability = (entryId, week) => {
		setDraftEntries((prev) =>
			prev.map((entry) => {
				if (entry.id !== entryId) return entry;
				const favorabilities = normalizeFavorabilities(entry.favorabilities);
				const key = String(week);
				return {
					...entry,
					favorabilities: {
						...favorabilities,
						[key]: nextFavorability(favorabilities[key])
					}
				};
			})
		);
	};

	const handleAddToRoster = (entryId) => {
		setRosterIds((prev) => {
			if (prev.includes(entryId)) return prev;
			return [...prev, entryId];
		});
		setActiveRail('roster');
	};

	const handleRemoveFromRoster = (entryId) => {
		setRosterIds((prev) => prev.filter((id) => id !== entryId));
	};

	const handleClearRoster = () => {
		setRosterIds([]);
	};

	const handleCreateRosterFromTeam = (teamIndex) => {
		const ids = getTeamSlotEntryIds(draftSlots || [], teamIndex);
		setRosterIds(ids);
		setActiveRail('roster');
	};

	const handleDraftSwap = (fromIndex, toIndex) => {
		setDraftSlots((prev) => swapDraftSlots(prev || [], fromIndex, toIndex));
	};

	const handleDraftReset = () => {
		setDraftSlots(seedDraftSlotsFromEntries(entries));
	};

	const handleSave = async () => {
		if (!canEdit || !update_eliminator_rankings_endpoint) return;

		const { ranks, favorabilities } = buildSparseUpdatePayload(
			editBaselineRef.current,
			draftEntries
		);
		const hasRankChanges = Object.keys(ranks).length > 0;
		const hasFavorabilityChanges = Object.keys(favorabilities).length > 0;

		if (!hasRankChanges && !hasFavorabilityChanges) {
			setDraftEntries([]);
			editBaselineRef.current = [];
			setIsEditing(false);
			setSuccessMessage('No changes to save.');
			return;
		}

		setIsSaving(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(
				buildEndpoint(update_eliminator_rankings_endpoint, {
					season: DEFAULT_SEASON
				}),
				{
					method: 'PATCH',
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						'X-CSRF-Token': getCsrfToken()
					},
					body: JSON.stringify({
						ranks: hasRankChanges ? ranks : undefined,
						favorabilities: hasFavorabilityChanges ? favorabilities : undefined
					})
				}
			);
			const payload = await response.json();

			if (!response.ok || !payload.success) {
				throw new Error(payload.error || 'Failed to save eliminator rankings.');
			}

			setEntries((prev) => mergePatchedEntries(prev, payload.data || []));
			setDraftEntries([]);
			editBaselineRef.current = [];
			setIsEditing(false);
			setSuccessMessage('Eliminator rankings saved.');
		} catch (saveError) {
			setError(saveError.message);
		} finally {
			setIsSaving(false);
		}
	};

	const handleExport = async () => {
		if (!canEdit || !export_eliminator_rankings_endpoint || entries.length === 0) {
			return;
		}

		setIsExporting(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(
				buildEndpoint(export_eliminator_rankings_endpoint, {
					season: DEFAULT_SEASON
				}),
				{ credentials: 'same-origin' }
			);

			if (!response.ok) {
				let message = 'Failed to export rankings.';
				try {
					const payload = await response.json();
					message = payload.error || message;
				} catch (jsonError) {
					// response body is not JSON
				}
				throw new Error(message);
			}

			const blob = await response.blob();
			const disposition = response.headers.get('Content-Disposition');
			const filenameMatch = disposition && disposition.match(/filename="?([^"]+)"?/);
			const filename =
				(filenameMatch && filenameMatch[1]) ||
				`eliminator-rankings-${DEFAULT_SEASON}.csv`;

			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);

			setSuccessMessage('Rankings exported.');
		} catch (exportError) {
			setError(exportError.message);
		} finally {
			setIsExporting(false);
		}
	};

	const handleFileChange = async (event) => {
		if (!canEdit) return;

		const file = event.target.files && event.target.files[0];
		event.target.value = '';
		if (!file || !import_eliminator_rankings_endpoint) return;

		if (!file.name.toLowerCase().endsWith('.csv')) {
			setError('Please upload a CSV file.');
			return;
		}

		setIsUploading(true);
		setError('');
		setSuccessMessage('');

		try {
			const csvContent = await file.text();
			const response = await fetch(import_eliminator_rankings_endpoint, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'X-CSRF-Token': getCsrfToken()
				},
				body: JSON.stringify({
					season: DEFAULT_SEASON,
					csv_content: csvContent
				})
			});
			const payload = await response.json();

			if (!response.ok || !payload.success) {
				throw new Error(payload.error || 'Failed to import rankings.');
			}

			const nextEntries = (payload.data.entries || []).map((entry) => ({
				...entry,
				favorabilities: normalizeFavorabilities(entry.favorabilities)
			}));

			setEntries(nextEntries);
			setDraftEntries([]);
			editBaselineRef.current = [];
			setIsEditing(false);
			setRosterIds([]);
			setDraftSlots(seedDraftSlotsFromEntries(nextEntries));
			draftSeededRef.current = true;

			const count = payload.data.entries_count;
			const truncatedNote =
				payload.data.truncated && payload.data.total_in_csv
					? ` (first ${count} of ${payload.data.total_in_csv} in file)`
					: '';
			setSuccessMessage(`Imported ${count} players${truncatedNote}.`);
		} catch (uploadError) {
			setError(uploadError.message);
		} finally {
			setIsUploading(false);
		}
	};

	const downloadCsvResponse = async (response, fallbackFilename) => {
		if (!response.ok) {
			let message = 'Failed to download CSV.';
			try {
				const payload = await response.json();
				message = payload.error || message;
			} catch (jsonError) {
				// response body is not JSON
			}
			throw new Error(message);
		}

		const blob = await response.blob();
		const disposition = response.headers.get('Content-Disposition');
		const filenameMatch = disposition && disposition.match(/filename="?([^"]+)"?/);
		const filename = (filenameMatch && filenameMatch[1]) || fallbackFilename;

		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
	};

	const handleExportDefenseMatrix = async () => {
		if (!canEdit || !export_eliminator_defense_matrix_endpoint) return;

		setIsExporting(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(export_eliminator_defense_matrix_endpoint, {
				credentials: 'same-origin'
			});
			await downloadCsvResponse(
				response,
				'eliminator-defense-matrix-2025.csv'
			);
			setSuccessMessage('Defense grade matrix exported.');
		} catch (exportError) {
			setError(exportError.message);
		} finally {
			setIsExporting(false);
		}
	};

	const handleExportDefenseGrades = async () => {
		if (
			!canEdit ||
			!export_eliminator_defense_grades_endpoint ||
			entries.length === 0
		) {
			return;
		}

		setIsExporting(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(
				buildEndpoint(export_eliminator_defense_grades_endpoint, {
					season: DEFAULT_SEASON
				}),
				{ credentials: 'same-origin' }
			);
			await downloadCsvResponse(
				response,
				`eliminator-defense-grades-${DEFAULT_SEASON}.csv`
			);
			setSuccessMessage('Defense matchup grades exported.');
		} catch (exportError) {
			setError(exportError.message);
		} finally {
			setIsExporting(false);
		}
	};

	const handleApplyDefenseGrades = async () => {
		if (!canEdit || !apply_eliminator_defense_grades_endpoint || entries.length === 0) {
			return;
		}

		const confirmed = window.confirm(
			'Apply defense-based favorability grades for all players and weeks? This replaces existing week grades (byes stay empty).'
		);
		if (!confirmed) return;

		setIsApplyingDefenseGrades(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(
				buildEndpoint(apply_eliminator_defense_grades_endpoint, {
					season: DEFAULT_SEASON
				}),
				{
					method: 'POST',
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						'X-CSRF-Token': getCsrfToken()
					},
					body: JSON.stringify({})
				}
			);
			const payload = await response.json();

			if (!response.ok || !payload.success) {
				throw new Error(payload.error || 'Failed to apply defense grades.');
			}

			const updated = (payload.data.entries || []).map((entry) => ({
				...entry,
				favorabilities: normalizeFavorabilities(entry.favorabilities)
			}));
			setEntries((prev) => mergePatchedEntries(prev, updated));
			setDraftEntries([]);
			editBaselineRef.current = [];
			setIsEditing(false);

			const dist = payload.data.distribution || {};
			setSuccessMessage(
				`Applied defense grades to ${payload.data.entries_count || updated.length} players` +
					` (favorable ${dist.favorable || 0}, neutral ${dist.neutral || 0}, unfavorable ${
						dist.unfavorable || 0
					}).`
			);
		} catch (applyError) {
			setError(applyError.message);
		} finally {
			setIsApplyingDefenseGrades(false);
		}
	};

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 lg:mb-6">
				<div>
					<PageTitle title="Eliminator Player Matchups" />
					<p className="text-sm text-gray-600 dark:text-gray-400 -mt-2">
						{DEFAULT_SEASON} Season | Eliminator | 0.5 PPR
					</p>
					<div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-700 dark:text-gray-300">
						{FAVORABILITY_ORDER.map((key) => (
							<span key={key} className="inline-flex items-center gap-2">
								<span
									className={`inline-block h-3 w-3 rounded-full ${FAVORABILITY[key].dotClass}`}
									aria-hidden="true"
								/>
								{FAVORABILITY[key].label}
							</span>
						))}
					</div>
				</div>

				{canEdit && (
					<div className="flex flex-wrap items-center gap-2">
						{!isEditingMode ? (
							<button
								type="button"
								className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
								onClick={handleStartEdit}
								disabled={entries.length === 0}
							>
								Edit
							</button>
						) : (
							<>
								<input
									ref={fileInputRef}
									type="file"
									accept=".csv,text/csv"
									className="hidden"
									onChange={handleFileChange}
								/>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={handleExport}
									disabled={
										isExporting ||
										isUploading ||
										isApplyingDefenseGrades ||
										entries.length === 0
									}
								>
									{isExporting ? 'Exporting…' : 'Export CSV'}
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={() =>
										fileInputRef.current && fileInputRef.current.click()
									}
									disabled={isUploading || isApplyingDefenseGrades}
								>
									{isUploading ? 'Uploading…' : 'Upload CSV'}
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={handleExportDefenseMatrix}
									disabled={
										isExporting || isUploading || isApplyingDefenseGrades
									}
								>
									D Matrix
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={handleExportDefenseGrades}
									disabled={
										isExporting ||
										isUploading ||
										isApplyingDefenseGrades ||
										entries.length === 0
									}
								>
									Export D Grades
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={handleApplyDefenseGrades}
									disabled={
										isApplyingDefenseGrades ||
										isUploading ||
										isExporting ||
										entries.length === 0
									}
								>
									{isApplyingDefenseGrades ? 'Applying…' : 'Apply D Grades'}
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={handleCancelEdit}
									disabled={isSaving || isApplyingDefenseGrades || isUploading}
								>
									Cancel
								</button>
								<button
									type="button"
									className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
									onClick={handleSave}
									disabled={isSaving || isApplyingDefenseGrades || isUploading}
								>
									{isSaving ? 'Saving…' : 'Save'}
								</button>
							</>
						)}
					</div>
				)}
			</div>

			{error && (
				<div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-300">
					{error}
				</div>
			)}

			{successMessage && (
				<div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-300">
					{successMessage}
				</div>
			)}

			{isEditingMode && (
				<div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-200">
					Drag rows to reorder. Click week dots to cycle favorable → neutral →
					unfavorable → clear.
				</div>
			)}

			<div className="relative z-40 flex flex-col lg:flex-row lg:mb-4 justify-between">
				<FilterByPosition
					filter={filter}
					onChange={handleFilterChange}
					hideLabel
					positionButtons={POSITION_BUTTONS}
				/>
				<FilterByTeamMultiSelect
					filter={filter}
					onChange={handleFilterChange}
					hideLabel
				/>
				<div className="hidden lg:contents">
					<FilterBySearchInput
						value={search}
						placeholder="Search for a player"
						onChange={handleSearchInputChange}
						onEnter={() => {}}
					/>
					<FilterByIsRookie filter={filter} onChange={handleFilterChange} />
					<ShowAvatarsToggle
						isChecked={showAvatars}
						onChange={setShowAvatars}
					/>
					<ShowWeeks15To17Toggle
						isChecked={showWeeks15To17}
						onChange={setShowWeeks15To17}
					/>
				</div>
				<div className="lg:hidden contents">
					<div className="flex items-start space-x-2 sm:space-x-4 w-full lg:w-auto">
						<FilterBySearchInput
							value={search}
							placeholder="Search for a player"
							onChange={handleSearchInputChange}
							onEnter={() => {}}
						/>
						<FilterByIsRookie filter={filter} onChange={handleFilterChange} />
						<ShowAvatarsToggle
							isChecked={showAvatars}
							onChange={setShowAvatars}
						/>
						<ShowWeeks15To17Toggle
							isChecked={showWeeks15To17}
							onChange={setShowWeeks15To17}
						/>
					</div>
				</div>
				<div className="hidden lg:contents">
					<ClearFiltersButton onClick={handleClearFilters} />
				</div>
			</div>

			{isLoading ? (
				<div className="flex justify-center py-16">
					<LoadingSpinner />
				</div>
			) : (
				<>
					<div className={`relative z-0 ${TABLE_WRAPPER_CLASS}`}>
						<table className="w-full border-collapse text-sm">
							<thead className={TABLE_HEAD_CLASS}>
								<tr>
									<th
										className={STICKY_HEAD_CELL_CLASS}
										style={{ left: stickyOffsets.rank, minWidth: '3.5rem' }}
									>
										Rank
									</th>
									<th
										className={`${STICKY_HEAD_CELL_CLASS} border-r border-gray-300 dark:border-dark-border`}
										style={{ left: stickyOffsets.player, minWidth: '11rem' }}
									>
										Player
									</th>
									<th
										className={`${STICKY_HEAD_CELL_CLASS} border-r border-gray-300 dark:border-dark-border`}
										style={{ left: stickyOffsets.pos, minWidth: '3.25rem' }}
									>
										Pos
									</th>
									{visibleWeeks.map((week) => (
										<th key={`week-head-${week}`} className={WEEK_HEAD_CELL_CLASS}>
											Week {week}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{displayEntries.length === 0 ? (
									<tr>
										<td
											colSpan={3 + visibleWeeks.length}
											className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
										>
											{entries.length === 0
												? 'No eliminator rankings yet. Seed with rake eliminator:seed_from_player_rankings or upload a CSV in Edit mode.'
												: 'No players match the current filters.'}
										</td>
									</tr>
								) : (
									displayEntries.map((entry) => (
										<PlayerMatchupRows
											key={entry.id}
											entry={entry}
											weeks={visibleWeeks}
											scheduleTeam={
												teamLookup[normalizeTeamKey(entry.team_name)] || null
											}
											teamsById={teamsById}
											showAvatars={showAvatars}
											stickyOffsets={stickyOffsets}
											isEditing={isEditingMode}
											isDragging={draggedId === entry.id}
											isDropTarget={dropTargetId === entry.id}
											isOnRoster={rosterIdSet.has(entry.id)}
											onAddToRoster={handleAddToRoster}
											onDragStart={() => setDraggedId(entry.id)}
											onDragOver={(event) => {
												event.preventDefault();
												setDropTargetId(entry.id);
											}}
											onDrop={(event) => {
												event.preventDefault();
												if (!draggedId) return;
												setDraftEntries((prev) =>
													reorderEntries(prev, draggedId, entry.id)
												);
												setDraggedId(null);
												setDropTargetId(null);
											}}
											onDragEnd={() => {
												setDraggedId(null);
												setDropTargetId(null);
											}}
											onCycleFavorability={handleCycleFavorability}
										/>
									))
								)}
							</tbody>
						</table>
					</div>

					{!isEditingMode && (
						<div className="relative mt-4 flex flex-col items-center gap-3 md:flex-row md:justify-center">
							<p className="text-sm text-gray-600 dark:text-gray-400 md:absolute md:left-0">
								Showing {displayEntries.length} of {filteredEntries.length} filtered
								({entries.length} total)
							</p>
							<div className="hidden md:block">
								<PaginationTracker
									currentPage={page}
									setPage={setPage}
									totalPages={totalPages}
								/>
							</div>
							<div className="md:hidden">
								<PaginationTrackerMobile
									currentPage={page}
									setPage={setPage}
									totalPages={totalPages}
								/>
							</div>
							<div className="md:absolute md:right-0">
								<PerPageDropdown perPage={perPage} setPerPage={setPerPage} />
							</div>
						</div>
					)}
				</>
			)}

			{activeRail == null && (
				<div className="fixed right-0 top-1/2 z-50 flex transform -translate-y-1/2 flex-col gap-2">
					<button
						type="button"
						onClick={() => setActiveRail('roster')}
						className="rounded-l-lg border border-r-0 border-gray-300 bg-white px-2 py-4 text-xs font-semibold text-gray-800 shadow-md hover:bg-gray-50 dark:border-dark-border dark:bg-dark-card dark:text-dark-text dark:hover:bg-gray-800"
						aria-label="Open roster rail"
					>
						<span className="transform rotate-180" style={{ writingMode: 'vertical-rl' }}>
							Roster{rosterIds.length > 0 ? ` (${rosterIds.length})` : ''}
						</span>
					</button>
					<button
						type="button"
						onClick={() => setActiveRail('draft')}
						className="rounded-l-lg border border-r-0 border-gray-300 bg-white px-2 py-4 text-xs font-semibold text-gray-800 shadow-md hover:bg-gray-50 dark:border-dark-border dark:bg-dark-card dark:text-dark-text dark:hover:bg-gray-800"
						aria-label="Open draft board rail"
					>
						<span className="transform rotate-180" style={{ writingMode: 'vertical-rl' }}>
							Draft
						</span>
					</button>
				</div>
			)}

			<EliminatorRosterRail
				isOpen={activeRail === 'roster'}
				onToggle={() => setActiveRail(null)}
				onSwitchToDraft={() => setActiveRail('draft')}
				rosterEntries={rosterEntries}
				weeks={visibleWeeks}
				teamLookup={teamLookup}
				teamsById={teamsById}
				onRemove={handleRemoveFromRoster}
				onClear={handleClearRoster}
			/>

			<EliminatorDraftBoardRail
				isOpen={activeRail === 'draft'}
				onClose={() => setActiveRail(null)}
				onSwitchToRoster={() => setActiveRail('roster')}
				slots={draftSlots || []}
				entriesById={entriesById}
				weeks={visibleWeeks}
				teamLookup={teamLookup}
				onSwap={handleDraftSwap}
				onReset={handleDraftReset}
				onCreateRosterFromTeam={handleCreateRosterFromTeam}
			/>
		</div>
	);
}
